import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="studio-tilt"
//
// Tilts the deck target in 3D toward the cursor. Ported from the Studio
// marketing dev-page hero: cursor normalized to -0.5..0.5 over the stage
// (this.element), mapped to rotateX/rotateY up to MAX degrees, with exponential
// smoothing on a requestAnimationFrame loop for an inertial feel. Pointer-only
// and disabled under prefers-reduced-motion. The deck reads --rx/--ry in its
// CSS transform.
export default class extends Controller {
  static targets = ["deck"];

  declare readonly deckTarget: HTMLElement;
  declare readonly hasDeckTarget: boolean;

  private readonly MAX = 7; // degrees
  private tx = 0;
  private ty = 0;
  private cx = 0;
  private cy = 0;
  private raf: number | null = null;
  private tracking = false;

  private onPointerMove = (e: PointerEvent): void => {
    const r = this.element.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5; // -0.5..0.5
    const py = (e.clientY - r.top) / r.height - 0.5;
    this.tx = px * this.MAX * 2; // rotateY (left/right)
    this.ty = -py * this.MAX * 2; // rotateX (up/down)
    if (!this.tracking) {
      this.tracking = true;
      this.deckTarget.classList.add("is-tracking");
    }
    this.kick();
  };

  private onPointerLeave = (): void => {
    this.tx = 0;
    this.ty = 0;
    this.tracking = false;
    this.deckTarget.classList.remove("is-tracking");
    this.kick();
  };

  connect(): void {
    if (!this.hasDeckTarget) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (window.matchMedia("(hover: none)").matches) return; // pointer-only effect

    this.element.addEventListener("pointermove", this.onPointerMove as EventListener);
    this.element.addEventListener("pointerleave", this.onPointerLeave);
  }

  disconnect(): void {
    this.element.removeEventListener("pointermove", this.onPointerMove as EventListener);
    this.element.removeEventListener("pointerleave", this.onPointerLeave);
    if (this.raf != null) {
      cancelAnimationFrame(this.raf);
      this.raf = null;
    }
  }

  private kick(): void {
    if (this.raf == null) this.raf = requestAnimationFrame(this.loop);
  }

  private loop = (): void => {
    this.cx += (this.tx - this.cx) * 0.12;
    this.cy += (this.ty - this.cy) * 0.12;
    this.deckTarget.style.setProperty("--rx", `${this.cx.toFixed(2)}deg`);
    this.deckTarget.style.setProperty("--ry", `${this.cy.toFixed(2)}deg`);
    if (Math.abs(this.tx - this.cx) > 0.01 || Math.abs(this.ty - this.cy) > 0.01) {
      this.raf = requestAnimationFrame(this.loop);
    } else {
      this.raf = null;
    }
  };
}
